import {Trans} from '@lingui/macro';
import {omit} from 'lodash';
import React, {Dispatch, FC, SetStateAction, useCallback} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ClearFiltersButton} from '../../../components/common/filters/common/clearFiltersButton';
import {FilterButton} from '../../../components/common/filters/common/filterButton';
import {FilterLabel} from '../../../components/common/filters/common/filterLabel';
import {SimpleFilterDropdown} from '../../../components/common/filters/simpleFilterDropdown';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsRulesUrl} from '../../../models/url/settingsUrlModel';
import {SettingsSearchInput} from '../../common/settingsSearchInput';
import {
  RULE_TYPES_LABELS,
  SettingsRuleListFiltersState,
  sortedRuleBundleCategories,
} from './ruleBundleCategory';
import {getRuleTypeIcon, getRuleTypePaletteColor} from './ruleBundleCategoryStyles';

/*
 * Props.
 */

export interface RuleListFiltersProps {
  value: SettingsRuleListFiltersState;
  onChange: Dispatch<SetStateAction<SettingsRuleListFiltersState>>;
}

/*
 * Styles.
 */

export const StyledFiltersWrapper = styled.div`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;

  // Flex gap for old Safari.
  margin: -8px 8px 0 -8px;
  > * {
    margin: 8px 0 0 8px;
  }
`;

const StyledSettingsSearchInput = styled(SettingsSearchInput)`
  width: 260px;
`;

/*
 * Component.
 */

export const RuleListFilters: FC<RuleListFiltersProps> = ({value, onChange}) => {
  const theme = useUserTheme();
  const updateFilters = (filters: Partial<SettingsRuleListFiltersState>) =>
    onChange((state) => ({...state, ...filters}));

  const hasFilters =
    Boolean(value.query) ||
    (value.categories && value.categories.length > 0) ||
    isDefined(value.onlySmart) ||
    isDefined(value.inbox);

  return (
    <StyledFiltersWrapper>
      <StyledSettingsSearchInput value={value.query} onChange={(query) => updateFilters({query})} />
      <SimpleFilterDropdown
        title={<Trans id="rule-list-filters-categories-title">Categories</Trans>}
        placeholder={
          <FilterLabel icon="gearSmall">
            <Trans id="rule-list-filters-categories-placeholder">Categories</Trans>
          </FilterLabel>
        }
        items={sortedRuleBundleCategories}
        selectedItems={value.categories ?? []}
        setSelectedItems={(ruleTypes) => updateFilters({categories: ruleTypes})}
        renderLabel={({key}) => RULE_TYPES_LABELS[key]}
        renderIcon={({key}, isInsideValue) => (
          <Icon
            name={isInsideValue ? getRuleTypeIcon(key).small.filled : getRuleTypeIcon(key).small.unfilled}
            color={isInsideValue ? theme.palette[getRuleTypePaletteColor(key)].shade40 : theme.greys.shade70}
          />
        )}
        onClear={() => updateFilters({categories: undefined})}
      />
      <FilterButton
        isActive={Boolean(value.onlySmart)}
        onClick={() =>
          onChange((state) => (state.onlySmart ? omit(state, 'onlySmart') : {...state, onlySmart: true}))
        }
      >
        <FilterLabel
          icon={value.onlySmart ? 'sparklesSmall' : 'sparklesOutlineSmall'}
          color={value.onlySmart ? theme.palette.blue.shade40 : 'currentColor'}
        >
          <Trans id="rule-list-filters-smart-rules-toggle">Smart rules</Trans>
        </FilterLabel>
      </FilterButton>
      {hasFilters && <ClearFiltersButton onClick={() => onChange({})} />}
    </StyledFiltersWrapper>
  );
};

/*
 * Hooks.
 */

export function useRuleListSearchState(url: SettingsRulesUrl) {
  const {navigateToLink} = useLinkNavigation();

  const setSearchState: Dispatch<SetStateAction<SettingsRuleListFiltersState>> = useCallback(
    (stateOrUpdater) => {
      const newFilters =
        typeof stateOrUpdater === 'function' ? stateOrUpdater(url.filters ?? {}) : stateOrUpdater;

      navigateToLink(
        buildSettingsPath({
          ...url,
          filters: cleanFilters(newFilters),
        }),
      );
    },
    [navigateToLink, url],
  );

  return [url.filters ?? {}, setSearchState] as const;
}

/*
 * Helpers.
 */

function cleanFilters(filters: SettingsRuleListFiltersState): SettingsRuleListFiltersState {
  const cleanQuery = filters.query === '' ? undefined : filters.query;
  const cleanCategories = filters.categories?.length === 0 ? undefined : filters.categories;

  return {
    ...filters,
    query: cleanQuery,
    categories: cleanCategories,
  };
}
